import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';

import { jwtDecode } from 'jwt-decode';

import { Region } from '../../../utils';
import { UseAuthContextType } from '../auth0/auth.types';
import { getDefaultUseAuthState } from '../auth0/helpers';
import {
  ConfidenceJwtPayload,
  getIdentityFromToken,
  getOrgNameFromToken,
  getRegionFromTokenOrDefault,
} from '../auth0/jwt';
import { useExchangeTokenApi } from './useExchangeTokenApi';

type PortalAuthContextType = UseAuthContextType;

const PortalAuthDefaultState = getDefaultUseAuthState({
  user: { name: 'portal', picture: undefined, email: 'email@email.com' },
});

const PortalAuthContext = createContext<PortalAuthContextType>(
  PortalAuthDefaultState,
);

interface AuthenticationProviderProps {
  children: ReactElement;
  onTokenReceived: (token: string, region: Region) => void;
}

export const AuthenticationProvider = ({
  onTokenReceived,
  children,
}: AuthenticationProviderProps) => {
  const tokenExchangeApi = useExchangeTokenApi();

  const [token, setToken] = useState<PortalAuthContextType['token']>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [cookies, setCookie] = useCookies(['conf_portal_tk']);

  const loginWithPopup = useCallback(async () => {
    if (token) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const response = await tokenExchangeApi.exchangeToken();

      if (response) {
        setIsLoading(false);
        const decoded = jwtDecode<ConfidenceJwtPayload>(response);
        setCookie('conf_portal_tk', response, {
          path: '/',
          expires: new Date(decoded.exp! * 1000),
        });
        setToken(response);
      } else {
        const authError = new Error(
          'We couldn’t complete the token exchange due to a Confidence authentication error',
        );
        setError(authError);
        setIsLoading(false);
      }
    } catch (e) {
      const authError = new Error(
        `Failed to auth with Confidence: ${(e as unknown as Error)?.message}`,
      );
      setError(authError);
      setIsLoading(false);
    }
  }, [tokenExchangeApi, setCookie]);

  const checkSession = async () => {
    const tokenItem = cookies.conf_portal_tk;
    if (tokenItem) {
      setIsLoading(false);
      setToken(tokenItem);
    } else {
      await loginWithPopup();
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  const value = useMemo<PortalAuthContextType>(
    () => ({
      ...PortalAuthDefaultState,
      isLoading,
      token,
      error,
      isAuthenticated: !!token,
      loginWithPopup,
      getAccessTokenSilently: async () => token ?? '',
      orgName: token ? getOrgNameFromToken(token) : undefined,
      identity: token ? getIdentityFromToken(token) : undefined,
    }),
    [PortalAuthDefaultState, token, loginWithPopup, isLoading, error],
  );

  useEffect(() => {
    if (value.token) {
      onTokenReceived(value.token, getRegionFromTokenOrDefault(value.token));
    }
  }, [value]);

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export const useAuth = () => useContext(PortalAuthContext);
